import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkIcon from '@mui/icons-material/Work';
import useScrollToTop from '../../hooks/useScrollToTop';

const Spain: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits = [
    { icon: <FlightIcon />, text: t('citizenship.spain.benefits.travel') },
    { icon: <CheckCircleIcon />, text: t('citizenship.spain.benefits.healthcare') },
    { icon: <WorkIcon />, text: t('citizenship.spain.benefits.work') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.spain.benefits.family') },
    { icon: <BusinessIcon />, text: t('citizenship.spain.benefits.business') },
    { icon: <SchoolIcon />, text: t('citizenship.spain.benefits.quality') },
  ];

  const requirements = [
    t('citizenship.spain.requirements.investment'),
    t('citizenship.spain.requirements.criminal'),
    t('citizenship.spain.requirements.financial'),
    t('citizenship.spain.requirements.insurance'),
    t('citizenship.spain.requirements.passport'),
    t('citizenship.spain.requirements.fee'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#f44336',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.spain.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.spain.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('citizenship.spain.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.spain.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.spain.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('citizenship.spain.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {benefit.icon}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('citizenship.spain.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%', minHeight: '400px' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.spain.requirements.investmentTitle')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%', minHeight: '400px' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.spain.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.spain.timeline.initial.title')}
                        secondary={t('citizenship.spain.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.spain.timeline.residence.title')}
                        secondary={t('citizenship.spain.timeline.residence.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.spain.timeline.permanent.title')}
                        secondary={t('citizenship.spain.timeline.permanent.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.spain.timeline.citizenship.title')}
                        secondary={t('citizenship.spain.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Spain; 